import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { WeeklyCheckIn } from '../types';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const CheckInDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [checkIn, setCheckIn] = useState<WeeklyCheckIn | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isTrainer = user?.role === 'trainer';

  useEffect(() => {
    if (id) {
      loadCheckIn(parseInt(id));
    }
  }, [id]);

  const loadCheckIn = async (checkInId: number) => {
    try {
      setLoading(true);
      const data = await apiService.getWeeklyCheckIn(checkInId);
      
      // For clients, ensure they can only view their own check-ins
      if (!isTrainer && data.client.user.id !== user?.id) {
        setError('You can only view your own check-ins');
        return;
      }
      
      setCheckIn(data);
    } catch (error) {
      console.error('Error loading check-in:', error);
      setError('Failed to load check-in details');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      weekday: 'long'
    });
  };

  const getTrafficLightColor = (rating: string) => {
    switch (rating) {
      case 'green': return 'bg-green-100 text-green-800 border-green-200';
      case 'amber': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'red': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getProgressColor = (progress: string) => {
    switch (progress) {
      case 'progressed': return 'bg-green-100 text-green-800 border-green-200';
      case 'stayed_same': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'regressed': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const StarRating: React.FC<{ value: number; label: string }> = ({ value, label }) => (
    <div className="flex items-center space-x-2">
      <span className="text-sm font-medium text-gray-700">{label}:</span>
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`text-lg ${
              star <= value ? 'text-yellow-400' : 'text-gray-300'
            }`}
          >
            ★
          </span>
        ))}
        <span className="ml-2 text-sm text-gray-500">({value}/5)</span>
      </div>
    </div>
  );

  const InfoSection: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div className="bg-white shadow rounded-lg p-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
      {children}
    </div>
  );

  const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
    <div className="flex justify-between items-start py-2 border-b border-gray-100 last:border-b-0">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <div className="text-sm text-gray-900 text-right max-w-xs">{value}</div>
    </div>
  );

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error || !checkIn) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-red-900 mb-2">Error</h2>
          <p className="text-red-700 mb-4">{error || 'Check-in not found'}</p>
          <button
            onClick={() => navigate('/checkins')}
            className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 transition-colors"
          >
            Back to Check-ins
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isTrainer ? 'Weekly Check-in Details' : 'My Weekly Check-in'}
            </h1>
            <p className="mt-2 text-gray-600">
              Week of {formatDate(checkIn.week_start_date)} • Submitted {formatDate(checkIn.submitted_at)}
            </p>
            {isTrainer && (
              <p className="text-sm text-gray-500">
                Client: {checkIn.client.user.first_name} {checkIn.client.user.last_name}
              </p>
            )}
          </div>
          <div className="flex space-x-3">
            {!isTrainer && (
              <Link
                to={`/checkins/${checkIn.id}/edit`}
                className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
              >
                Edit
              </Link>
            )}
            <button
              onClick={() => navigate('/checkins')}
              className="bg-gray-600 text-white px-4 py-2 rounded-md hover:bg-gray-700 transition-colors"
            >
              Back
            </button>
          </div>
        </div>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Overall Grade</h3>
          <div className="mt-2 flex items-center">
            <span className="text-3xl font-bold text-gray-900">{checkIn.overall_grade}</span>
            <span className="ml-2 text-yellow-400 text-2xl">★</span>
          </div>
        </div>
        
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Status</h3>
          <div className="mt-2">
            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full border ${getTrafficLightColor(checkIn.traffic_light_rating ?? '')}`}>
              {checkIn.traffic_light_rating
                ? checkIn.traffic_light_rating.charAt(0).toUpperCase() + checkIn.traffic_light_rating.slice(1)
                : '-'}
            </span>
          </div>
        </div>
        
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Progress</h3>
          <div className="mt-2">
            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full border ${getProgressColor(checkIn.progress_assessment ?? '')}`}>
              {checkIn.progress_assessment
                ? checkIn.progress_assessment.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())
                : '-'}
            </span>
          </div>
        </div>
        
        <div className="bg-white shadow rounded-lg p-6">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide">Weight</h3>
          <div className="mt-2">
            <span className="text-3xl font-bold text-gray-900">
              {checkIn.weight_kg ? `${checkIn.weight_kg} kg` : 'N/A'}
            </span>
          </div>
        </div>
      </div>

      {/* Custom Check-In Questions */}
      {checkIn.custom_answers_display && checkIn.custom_answers_display.length > 0 && (
        <InfoSection title="Check-In Responses">
          <div className="space-y-4">
            {checkIn.custom_answers_display.map((item) => (
              <div key={item.question_id} className="py-3 border-b border-gray-100 last:border-b-0">
                <p className="text-sm font-medium text-gray-700 mb-1">{item.question_text}</p>
                <div className="text-sm text-gray-900">
                  {item.field_type === 'rating' && typeof item.answer === 'number' ? (
                    <div className="flex items-center gap-1">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <span
                          key={star}
                          className={star <= item.answer ? 'text-yellow-400' : 'text-gray-300'}
                        >
                          ★
                        </span>
                      ))}
                      <span className="ml-2 text-gray-500">({item.answer}/5)</span>
                    </div>
                  ) : Array.isArray(item.answer) ? (
                    <p>{item.answer.join(', ')}</p>
                  ) : (
                    <p className="whitespace-pre-wrap">{String(item.answer)}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </InfoSection>
      )}

      {/* Consistency Ratings */}
      <InfoSection title="Consistency Ratings">
        <div className="space-y-4">
          <StarRating value={checkIn.nutrition_consistency} label="Nutrition Consistency" />
          <StarRating value={checkIn.step_count_consistency} label="Step Count Consistency" />
          <StarRating value={checkIn.training_consistency} label="Training Consistency" />
          <StarRating value={checkIn.self_confidence_rating} label="Self Confidence" />
          <div className="pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700">Average Consistency:</span>
              <span className="text-lg font-semibold text-gray-900">
                {checkIn.average_consistency_rating.toFixed(1)}/5
              </span>
            </div>
          </div>
        </div>
      </InfoSection>

      {/* Progress Assessment */}
      <InfoSection title="Progress Assessment">
        <div className="space-y-4">
          <InfoRow
            label="Progress Assessment"
            value={
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getProgressColor(checkIn.progress_assessment ?? '')}`}>
                {checkIn.progress_assessment
                  ? checkIn.progress_assessment.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())
                  : '-'}
              </span>
            }
          />
          {checkIn.progress_assessment === 'regressed' && checkIn.regression_reason && (
            <InfoRow
              label="Regression Reason"
              value={checkIn.regression_reason}
            />
          )}
          <InfoRow
            label="Traffic Light Rating"
            value={
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border ${getTrafficLightColor(checkIn.traffic_light_rating ?? '')}`}>
                {checkIn.traffic_light_rating
                  ? checkIn.traffic_light_rating.charAt(0).toUpperCase() + checkIn.traffic_light_rating.slice(1)
                  : '-'}
              </span>
            }
          />
        </div>
      </InfoSection>

      {/* Weekly Reflection */}
      <InfoSection title="Weekly Reflection">
        <div className="space-y-4">
          {checkIn.training_struggles && (
            <InfoRow
              label="Training Struggles"
              value={checkIn.training_struggles}
            />
          )}
          {checkIn.deviations && (
            <InfoRow
              label="Deviations"
              value={checkIn.deviations}
            />
          )}
          {checkIn.biggest_win && (
            <InfoRow
              label="Biggest Win"
              value={checkIn.biggest_win}
            />
          )}
          {checkIn.nutrition_experiences && (
            <InfoRow
              label="Nutrition Experiences"
              value={checkIn.nutrition_experiences}
            />
          )}
          {checkIn.digestion_notes && (
            <InfoRow
              label="Digestion Notes"
              value={checkIn.digestion_notes}
            />
          )}
          {checkIn.goals_for_next_week && (
            <InfoRow
              label="Goals for Next Week"
              value={checkIn.goals_for_next_week}
            />
          )}
          {checkIn.questions_for_trainer && (
            <InfoRow
              label="Questions for Trainer"
              value={checkIn.questions_for_trainer}
            />
          )}
          {checkIn.additional_help && (
            <InfoRow
              label="Additional Help Needed"
              value={checkIn.additional_help}
            />
          )}
          {checkIn.areas_for_improvement && (
            <InfoRow
              label="Areas for Improvement"
              value={checkIn.areas_for_improvement}
            />
          )}
          {checkIn.anything_else && (
            <InfoRow
              label="Anything Else"
              value={checkIn.anything_else}
            />
          )}
        </div>
      </InfoSection>

      {/* Female Health (if applicable) */}
      {(checkIn.menstrual_cycle_day || checkIn.female_health_issues) && (
        <InfoSection title="Female Health Information">
          <div className="space-y-4">
            {checkIn.menstrual_cycle_day && (
              <InfoRow
                label="Menstrual Cycle Day"
                value={`Day ${checkIn.menstrual_cycle_day}`}
              />
            )}
            {checkIn.female_health_issues && (
              <InfoRow
                label="Health Issues"
                value={checkIn.female_health_issues}
              />
            )}
          </div>
        </InfoSection>
      )}
    </div>
  );
};

export default CheckInDetail; 